<script setup lang="ts">
import { LucideTimer } from 'lucide-vue-next'
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import type { IQuestion, IUserAnswer, IUserAnswered } from './types'

const props = defineProps<{
  question: IQuestion
  currentIndex: number
  totalCount: number | null
  secondsLeft: number | null
  isSubmitting: boolean
  prefilledAnswer?: IUserAnswered | null
  canGoBack?: boolean
}>()

const emit = defineEmits<{
  submit: [answer: IUserAnswer]
  skip: []
  back: []
}>()

const selectedRadio = ref<string | undefined>(undefined)
const selectedCheckboxes = ref<number[]>([])
const textAnswer = ref('')

const formattedTime = computed(() => {
  if (props.secondsLeft === null) return null
  const m = Math.floor(props.secondsLeft / 60)
  const s = props.secondsLeft % 60
  return `${m}:${s.toString().padStart(2, '0')}`
})

const timerUrgent = computed(() => props.secondsLeft !== null && props.secondsLeft <= 30)

const canSubmit = computed(() => {
  if (props.question.type === 'radio') return selectedRadio.value !== undefined
  if (props.question.type === 'checkbox') return selectedCheckboxes.value.length > 0
  return textAnswer.value.trim().length > 0
})

const toggleCheckbox = (id: number) => {
  const max = props.question.correct_answers_count
  const idx = selectedCheckboxes.value.indexOf(id)
  if (idx >= 0) {
    selectedCheckboxes.value.splice(idx, 1)
  } else {
    if (max !== null && selectedCheckboxes.value.length >= max) return
    selectedCheckboxes.value.push(id)
  }
}

const handleSubmit = () => {
  const answer: IUserAnswer = { question_id: props.question.id }
  if (props.question.type === 'radio') answer.answer_id = Number(selectedRadio.value)
  if (props.question.type === 'checkbox') answer.answer_ids = [...selectedCheckboxes.value]
  if (props.question.type === 'text') answer.text_answer = textAnswer.value.trim()
  emit('submit', answer)
}

const applyPrefill = () => {
  const a = props.prefilledAnswer
  if (!a) {
    selectedRadio.value = undefined
    selectedCheckboxes.value = []
    textAnswer.value = ''
    return
  }
  selectedRadio.value =
    props.question.type === 'radio' && a.answer_id != null ? String(a.answer_id) : undefined
  const rawText = a.text_answer
  if (props.question.type === 'checkbox' && rawText) {
    try {
      selectedCheckboxes.value = JSON.parse(rawText)
    } catch {
      selectedCheckboxes.value = []
    }
  } else {
    selectedCheckboxes.value = []
  }
  textAnswer.value = props.question.type === 'text' ? (rawText ?? '') : ''
}

watch([() => props.question.id, () => props.prefilledAnswer], applyPrefill, { immediate: true })
</script>

<template>
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white p-5">
    <!-- Top row: tag + timer -->
    <div class="mb-4 flex items-center gap-2">
      <span
        class="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600"
      >
        {{ $t('widget.quiz.question.tag') }}
      </span>
      <span
        v-if="formattedTime !== null"
        class="ml-auto flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition-colors"
        :class="
          timerUrgent
            ? 'border-red-200 bg-red-50 text-red-600'
            : 'border-amber-200 bg-amber-50 text-amber-700'
        "
      >
        <LucideTimer class="h-3 w-3" />
        {{ formattedTime }}
      </span>
    </div>

    <!-- Step dots -->
    <div v-if="totalCount" class="mb-4 flex gap-1.5">
      <span
        v-for="i in totalCount"
        :key="i"
        class="h-1 flex-1 rounded-full transition-colors"
        :class="i <= currentIndex + 1 ? 'bg-blue-600' : 'bg-gray-200'"
      />
    </div>

    <!-- Question -->
    <p v-if="totalCount" class="mb-1 text-xs text-gray-400">
      {{ currentIndex + 1 }} {{ $t('widget.quiz.question.of') }} {{ totalCount }}
    </p>
    <div class="mb-4">
      <h4 class="text-base font-bold text-gray-900">{{ question.title }}</h4>
      <p v-if="question.description?.trim()" class="mt-1 text-sm text-gray-800">{{ question.description }}</p>
    </div>

    <!-- Radio options -->
    <RadioGroup
      v-if="question.type === 'radio'"
      v-model="selectedRadio"
      class="flex flex-col gap-2"
    >
      <label
        v-for="answer in question.answers"
        :key="answer.id"
        class="flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
        :class="
          selectedRadio === String(answer.id)
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        "
      >
        <RadioGroupItem
          :value="String(answer.id)"
          class="flex-shrink-0"
        />
        <span
          class="flex-1 text-sm"
          :class="
            selectedRadio === String(answer.id) ? 'font-medium text-blue-800' : 'text-gray-700'
          "
        >
          {{ answer.text }}
        </span>
      </label>
    </RadioGroup>

    <!-- Checkbox options -->
    <div v-else-if="question.type === 'checkbox'" class="flex flex-col gap-2">
      <p v-if="question.correct_answers_count" class="mb-1 text-xs text-gray-400">
        {{ $t('widget.quiz.question.choose_count', { n: question.correct_answers_count }) }}
      </p>
      <label
        v-for="answer in question.answers"
        :key="answer.id"
        class="flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
        :class="
          selectedCheckboxes.includes(answer.id)
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        "
      >
        <Checkbox
          :model-value="selectedCheckboxes.includes(answer.id)"
          class="flex-shrink-0"
          @update:model-value="toggleCheckbox(answer.id)"
        />
        <span
          class="text-sm"
          :class="
            selectedCheckboxes.includes(answer.id) ? 'font-medium text-blue-800' : 'text-gray-700'
          "
        >
          {{ answer.text }}
        </span>
      </label>
    </div>

    <!-- Text input -->
    <div v-else-if="question.type === 'text'">
      <Input
        v-model="textAnswer"
        type="text"
        class="h-11"
        :placeholder="$t('widget.quiz.question.text_placeholder')"
      />
    </div>

    <!-- Footer actions -->
    <div class="mt-5 flex items-center justify-between gap-2">
      <Button v-if="canGoBack" variant="outline-gray" @click="emit('back')">
        ← <span class="hidden sm:inline">{{ $t('widget.quiz.question.back') }}</span>
      </Button>
      <div v-else />
      <div class="flex items-center gap-2">
        <Button v-if="question.can_skip" variant="outline-gray" @click="emit('skip')">
          {{ $t('widget.quiz.question.skip') }}
        </Button>
        <Button variant="blue" :disabled="!canSubmit || isSubmitting" @click="handleSubmit">
          {{ $t('widget.quiz.question.submit') }}
        </Button>
      </div>
    </div>
  </div>
</template>
